import React, { useEffect, useState } from 'react';
import type { MenuItem } from '../types';
import { Link, withPrefix } from 'gatsby';

import {
  restDocs,
  webDocs,
  iosDocs,
  androidDocs,
  gettingStartedMenu,
  unityDocs,
  edgeauthDocs,
  referenceMenu,
  faqMenu,
  troubleshootMenu,
  howToMenu,
  reactNativeDocs,
} from '../../menus';
import './Pagination.styles.scss';

export type Menu = {
  match: string;
  menu: MenuItem[];
};

export interface PaginationProps {
  path: string;
  pageTitle: boolean;
  excludePath?: string[];
}

interface ButtonProps {
  title?: string;
  path?: string;
}

interface NavButtonState {
  next?: ButtonProps;
  prev?: ButtonProps;
}

/**
 * Recursively flatten an array of MenuItems
 */
function flatten(arr: MenuItem[]): MenuItem[] {
  return arr.reduce((flattened: MenuItem[], { path, label, submenu }) => {
    return flattened
      .concat([{ path, label }])
      .concat(submenu ? flatten(submenu) : []);
  }, []);
}

/**
 * Removes all trailing slashes from a string for more accurate path comparisons
 */
function removeTrailingSlash(str: string) {
  return str.replace(/\/+$/, '');
}

/**
 * An array of menu objects for each of the subdirectories in `src/pages`. Uses the
 * `match` key to identify the current subdirectory
 */
const menus: Menu[] = [
  {
    match: '/web/',
    menu: flatten(webDocs),
  },
  {
    match: '/ios/',
    menu: flatten(iosDocs),
  },
  {
    match: '/rest-api/',
    menu: flatten(restDocs),
  },
  {
    match: '/android/',
    menu: flatten(androidDocs),
  },
  {
    match: '/unity/',
    menu: flatten(unityDocs),
  },
  {
    match: '/react-native/',
    menu: flatten(reactNativeDocs),
  },
  {
    match: '/edgeauth/',
    menu: flatten(edgeauthDocs),
  },
  {
    match: '/how-to/',
    menu: flatten(howToMenu),
  },
  {
    match: '/faqs/',
    menu: flatten(faqMenu),
  },
  {
    match: '/reference/',
    menu: flatten(referenceMenu),
  },
  {
    match: '/troubleshooting/',
    menu: flatten(troubleshootMenu),
  },
  {
    match: '/getting-started/',
    menu: flatten(gettingStartedMenu),
  },
];

/**
 * The `Pagination` component accepts an array of menus and the current path to create
 * a Previous and Next button for post navigation
 */
export const Pagination = ({ path, pageTitle }: PaginationProps) => {
  const [key, setKey] = useState('');
  const [navButtons, setNavButtons] = useState<NavButtonState>({});

  function findPath() {
    const p = menus.find((i) => path?.includes(i.match))?.match;
    return p ? p : '';
  }

  /**
   * Update the current menu directory state via `key`
   */
  useEffect(() => {
    // Initial check
    if (key === '') {
      setKey(findPath());
    }
    // All subsequent changes in page routes
    if (key !== '' && !path.includes(key)) {
      setKey(findPath());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key]);

  /**
   * Update the next / previous links via `navButtons`
   */
  useEffect(() => {
    if (key && menus) {
      // Look up current menu
      const current = menus.find((i) => i.match === key);

      // If the current menu exists, determine the next / previous pages
      if (current) {
        const length = current?.menu.length;
        const index = current?.menu.findIndex(
          (i) => withPrefix(i.path ?? '') === removeTrailingSlash(path)
        );

        setNavButtons({
          prev:
            index !== 0
              ? {
                  title: current?.menu[index - 1]?.label,
                  path: current?.menu[index - 1]?.path,
                }
              : undefined,
          next:
            index !== length - 1
              ? {
                  title: current?.menu[index + 1]?.label,
                  path: current?.menu[index + 1]?.path,
                }
              : undefined,
        });
      }
    }
  }, [path, key]);

  return key !== '' ? (
    <div className="post-pagination flex justify-between">
      {navButtons.prev?.path ? (
        <Link className="pagination prev" to={navButtons.prev.path}>
          <span className="pagination-label">Previous</span>
          {pageTitle && (
            <span className="pagination-title">{navButtons.prev.title}</span>
          )}
        </Link>
      ) : (
        <div />
      )}
      {navButtons.next?.path ? (
        <Link className="pagination next" to={navButtons.next.path}>
          <span className="pagination-label">Next</span>
          {pageTitle && (
            <span className="pagination-title">{navButtons.next.title}</span>
          )}
        </Link>
      ) : (
        <div />
      )}
    </div>
  ) : null;
};
